import React, { useState, useEffect } from "react";
import "../Assests/Dashboard.css";
import DashboardTab from "../Components/Dashboard";
import Calendar from "../Components/Calendar";
import OverviewContent from "../Components/OverviewContent";
import { supabase } from "../Db_Connections/db_connection";
import { getLeaves, getLeavesByProfile } from "../Db_Connections/leavesApi";
import HierarchyPage from "../Components/HierarchyPage";
import {
  getAttendance,
  getAttendanceByProfile,
  getTodayAttendance,
  checkInUser,
  checkOutUser,
  autoClosePastSessions,
} from "../Db_Connections/AttendanceApi";

const initialEditForm = {
  first_name: "",
  last_name: "",
  email: "",
  personal_email: "",
  phone: "",
  dob: "",
  gender: "",
  designation: "",
  department: "",
  employment_type: "",
  joining_date: "",
  location: "",
  manager_name: "",
  role: "",
  temp_password: "",
  notes: "",
  is_active: true,
};

function Overview() {
  const mainTabs = ["Overview", "Dashboard", "Calendar", "Org Chart"];
  const overviewTabs = ["Activities", "Profile", "Leave", "Attendance"];
  const dashboardTabs = ["Home"];
  const calendarTabs = ["Month", "Week", "Day"];

  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [reporters, setReporters] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState({ present: 0 });

  // UI States
  const [activeMainTab, setActiveMainTab] = useState("Dashboard");
  const [overviewTab, setOverviewTab] = useState("Activities");
  const [dashboardTab, setDashboardTab] = useState("Home");
  const [calendarTab, setCalendarTab] = useState("Month");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [todayRecord, setTodayRecord] = useState(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [attendanceFilter, setAttendanceFilter] = useState("This Month");
  const [selectedAttendanceEmployee, setSelectedAttendanceEmployee] =
    useState("");

  // Timer State
  const [todayCheckedInIds, setTodayCheckedInIds] = useState([]);

  // ✅ ADDED: Internship certificate eligibility helpers
  const INTERNSHIP_DURATION_MONTHS = 6; // <-- apni policy ke hisab se change kar lena

  const toLocalDateOnly = (val) => {
    if (!val) return null;

    // handle "YYYY-MM-DD"
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      dt.setHours(0, 0, 0, 0);
      return dt;
    }

    const dt = new Date(val);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };

  const monthKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  const calcExtraLeaveDaysSinceJoining = (profileId, joiningDateStr) => {
    const joining = toLocalDateOnly(joiningDateStr);
    if (!joining) return 0;

    const usedByMonth = {}; // { "YYYY-MM": usedDays }
    const relevant = (leaveHistory || []).filter(
      (l) =>
        l.profile_id === profileId &&
        l.status !== "Rejected" &&
        (l.leave_type === "Casual Leave" || l.leave_type === "Sick Leave"),
    );

    relevant.forEach((l) => {
      const s0 = toLocalDateOnly(l.start_date);
      const e0 = toLocalDateOnly(l.end_date);
      if (!s0 || !e0) return;

      // joining se pehle wala part ignore
      const start = s0 < joining ? new Date(joining) : new Date(s0);
      const end = new Date(e0);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = monthKey(d);
        usedByMonth[key] = (usedByMonth[key] || 0) + 1; // per-day count
      }
    });

    // Per month 2 days allowed, usse zyada = extra days
    let extra = 0;
    Object.values(usedByMonth).forEach((usedDays) => {
      extra += Math.max(0, usedDays - 2);
    });

    return extra;
  };

  const getInternshipEligibility = (profile) => {
    const joining = toLocalDateOnly(profile?.joining_date);
    if (!joining) return null;

    const plannedEnd = new Date(joining);
    plannedEnd.setMonth(plannedEnd.getMonth() + INTERNSHIP_DURATION_MONTHS);

    const autoExtraDays = calcExtraLeaveDaysSinceJoining(
      profile.id,
      profile.joining_date,
    );

    // ✅ ADDED: Manual extra days (Admin can add if leave was informed via message but not applied)
    // NOTE: iske liye profiles table me `manual_extra_days` (integer) column hona chahiye.
    const manualExtraDays = Number(profile?.manual_extra_days || 0);

    const extraDays = autoExtraDays + manualExtraDays;

    const actualEnd = new Date(plannedEnd);
    actualEnd.setDate(actualEnd.getDate() + extraDays);

    const today = toLocalDateOnly(new Date());
    const remainingDays = Math.max(
      0,
      Math.ceil(
        (actualEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      ),
    );

    return {
      autoExtraDays,
      manualExtraDays,
      extraDays,
      plannedEnd,
      actualEnd,
      remainingDays,
    };
  };
  // ✅ ADDED END

  // ✅ ADDED: Admin manual extra days UI state + save handler
  const [manualDaysDraft, setManualDaysDraft] = useState({});
  const [savingManualDaysId, setSavingManualDaysId] = useState(null);

  const saveManualExtraDays = async (emp) => {
    try {
      const raw = manualDaysDraft?.[emp.id];
      const days = Math.max(0, parseInt(raw ?? emp?.manual_extra_days ?? 0, 10) || 0);

      setSavingManualDaysId(emp.id);

      const { error } = await supabase
        .from("profiles")
        .update({ manual_extra_days: days })
        .eq("id", emp.id);

      if (error) throw error;

      setReporters((prev) =>
        prev.map((p) =>
          p.id === emp.id ? { ...p, manual_extra_days: days } : p,
        ),
      );

      if (currentUser?.id === emp.id) {
        setCurrentUser((prev) => ({ ...prev, manual_extra_days: days }));
      }
    } catch (e) {
      console.error("Save manual extra days error:", e);
      alert(e?.message || "Failed to save manual extra days");
    } finally {
      setSavingManualDaysId(null);
    }
  };
  // ✅ ADDED END

  // ==================== FETCH DATA ====================
  useEffect(() => {
    const fetchAllData = async () => {
      const sessionData = sessionStorage.getItem("auth");
      if (!sessionData) return;

      const sessionUser = JSON.parse(sessionData);
      const adminRole = sessionUser.role === "Admin";
      setIsAdmin(adminRole);
      setLoading(true);

      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("email", sessionUser.email)
          .single();

        // if (profile) {
        //   setCurrentUser(profile);

        //   // B. Team Members
        //   // ✅ CHANGED: Admin ko sab employees dikhane ke liye limit remove
        //   let teamQuery = supabase.from("profiles").select("*");
        //   if (!adminRole) {
        //     teamQuery = teamQuery.eq("department", profile.department).limit(10);
        //     // ✅ FIXED: Removed .neq('email', profile.email) to include current user
        //   }
        //   const { data: team } = await teamQuery;

        //   // ✅ FIXED: For non-admin, ensure current user is first in list
        //   let finalTeam = team || [];
        //   if (!adminRole) {
        //     finalTeam = finalTeam.filter((p) => p.id !== profile.id);
        //     finalTeam = [profile, ...finalTeam].slice(0, 10);
        //   }

        //   setReporters(finalTeam);
        if (profile) {
          setCurrentUser(profile);

          // B. Team Members / All Employees
          let teamQuery = supabase.from("profiles").select("*");

          const { data: team } = await teamQuery;

          let finalTeam = team || [];

          setReporters(finalTeam);
        
        }
        // F. Today's checked-in members
        const todayStr = new Date().toISOString().split("T")[0];

        const { data: todayAttendance, error: todayAttendanceError } =
          await supabase
            .from("attendance")
            .select(
              "profile_id, check_in_time, check_out_time, attendance_date",
            )
            .eq("attendance_date", todayStr)
            .not("check_in_time", "is", null);

        if (todayAttendanceError) {
          console.error(
            "Error fetching today attendance:",
            todayAttendanceError,
          );
        } else {
          const checkedInIds = [
            ...new Set((todayAttendance || []).map((item) => item.profile_id)),
          ];
          setTodayCheckedInIds(checkedInIds);
        }

        let leavesRes;
        if (adminRole) leavesRes = await getLeaves();
        else leavesRes = await getLeavesByProfile(sessionUser.id);
        setLeaveHistory(leavesRes.data || []);

        let attendanceRes;
        if (adminRole) attendanceRes = await getAttendance();
        else attendanceRes = await getAttendanceByProfile(sessionUser.id);

        setAttendanceData(attendanceRes.data || []);

        if (attendanceRes.data) {
          const presentCount = attendanceRes.data.filter(
            (a) => a.status === "Present",
          ).length;
          setAttendanceSummary({ present: presentCount });
        }

        await autoClosePastSessions(sessionUser.id);

        const { data: today } = await getTodayAttendance(sessionUser.id);

        if (today) {
          setTodayRecord(today);
          if (today.check_in_time && !today.check_out_time) {
            const startTime = new Date(today.check_in_time).getTime();
            const now = Date.now();
            const diffSec = Math.floor((now - startTime) / 1000);
            setElapsedSeconds(diffSec > 0 ? diffSec : 0);
            setIsTimerRunning(true);
          } else if (today.working_hours) {
            setIsTimerRunning(false);
            setElapsedSeconds(Number(today.working_hours) * 3600);
          } else {
            setIsTimerRunning(false);
            setElapsedSeconds(0);
          }
        } else {
          setTodayRecord(null);
          setIsTimerRunning(false);
          setElapsedSeconds(0);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let intervalId;
    if (isTimerRunning) {
      intervalId = setInterval(
        () => setElapsedSeconds((prev) => prev + 1),
        1000,
      );
    }
    return () => clearInterval(intervalId);
  }, [isTimerRunning]);

  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      personal_email: user.personal_email || "",
      phone: user.phone || "",
      dob: user.dob ? String(user.dob).slice(0, 10) : "",
      gender: user.gender || "",
      designation: user.designation || "",
      department: user.department || "",
      employment_type: user.employment_type || "",
      joining_date: user.joining_date
        ? String(user.joining_date).slice(0, 10)
        : "",
      location: user.location || "",
      manager_name: user.manager_name || "",
      role: user.role || "",
      temp_password: "",
      notes: user.notes || "",
      is_active: user.is_active ?? true,
    });
  };

  const closeEdit = () => {
    setEditingUser(null);
    setEditForm(initialEditForm);
  };

  const handleEditChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveEdit = async () => {
    if (!editingUser) return;
    setSaving(true);

    try {
      const payload = { ...editForm };
      delete payload.employee_code;
      delete payload.employeeCode;

      const { error } = await supabase
        .from("profiles")
        .update(payload)
        .eq("id", editingUser.id);

      if (error) throw error;

      setReporters((prev) =>
        prev.map((u) => (u.id === editingUser.id ? { ...u, ...payload } : u)),
      );

      if (currentUser?.id === editingUser.id) {
        setCurrentUser((prev) => ({ ...prev, ...payload }));
      }

      closeEdit();
    } catch (e) {
      console.error("Update error:", e);
      alert(e?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (user) => {
    const ok = window.confirm(
      `Are you sure you want to delete ${user.first_name} ${user.last_name}?`,
    );
    if (!ok) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .delete()
        .eq("id", user.id);

      if (error) throw error;

      setReporters((prev) => prev.filter((u) => u.id !== user.id));

      if (editingUser?.id === user.id) closeEdit();
      if (selectedProfile?.id === user.id) setSelectedProfile(null);
    } catch (e) {
      console.error("Delete error:", e);
      alert(e?.message || "Delete failed");
    }
  };

  const formatTime = (totalSeconds) => {
    const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
    const s = String(totalSeconds % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const handleCheckInClick = async (e) => {
    e.stopPropagation();

    const authStr = sessionStorage.getItem("auth");
    if (!authStr) return;
    const auth = JSON.parse(authStr);

    if (!isTimerRunning) {
      const { data, error } = await checkInUser(auth.id);
      if (error) {
        alert("Error Checking In: " + error.message);
        return;
      }
      setTodayRecord(data);
      setElapsedSeconds(0);
      setIsTimerRunning(true);
    } else {
      const { data, error } = await checkOutUser(todayRecord.id);
      if (error) {
        alert("Error Checking Out: " + error.message);
        return;
      }
      setIsTimerRunning(false);
      setTodayRecord(data);
      if (data.working_hours) {
        setElapsedSeconds(Number(data.working_hours) * 3600);
      }
    }
  };

  const handleProfileClick = (profile) => {
    if (!isAdmin && profile.id !== currentUser?.id) return;
    setSelectedProfile(profile);
  };

  const handleBack = () => setSelectedProfile(null);

  const handleMainTabChange = (tab) => {
    setActiveMainTab(tab);
    setSelectedProfile(null);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    setUploadingAvatar(true);

    try {
      const ext = file.name.split(".").pop();
      const filePath = `${currentUser.id}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      const avatarUrl = publicUrlData.publicUrl;

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: avatarUrl })
        .eq("id", currentUser.id);

      if (updateError) throw updateError;

      setCurrentUser((prev) => ({ ...prev, avatar_url: avatarUrl }));
      setReporters((prev) =>
        prev.map((u) =>
          u.id === currentUser.id ? { ...u, avatar_url: avatarUrl } : u,
        ),
      );
    } catch (err) {
      alert(err.message || "Failed to upload profile photo");
    } finally {
      setUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const handleAvatarDelete = async () => {
    if (!currentUser?.avatar_url) return;

    setUploadingAvatar(true);

    try {
      const avatarUrl = currentUser.avatar_url;
      const split = avatarUrl.split("/avatars/");
      const path = split.length === 2 ? split[1] : null;

      if (path) {
        const { error: removeError } = await supabase.storage
          .from("avatars")
          .remove([path]);

        if (removeError)
          console.warn("Storage remove error (ignored):", removeError);
      }

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", currentUser.id);

      if (updateError) throw updateError;

      setCurrentUser((prev) => ({ ...prev, avatar_url: null }));
      setReporters((prev) =>
        prev.map((u) =>
          u.id === currentUser.id ? { ...u, avatar_url: null } : u,
        ),
      );
    } catch (err) {
      alert(err.message || "Failed to delete photo");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const openImagePreview = (url) => {
    if (url) setPreviewImage(url);
  };

  const closeImagePreview = () => setPreviewImage(null);

  const FIELD_LABELS = {
    id: "Employee ID",
    first_name: "First Name",
    last_name: "Last Name",
    email: "Work Email",
    personal_email: "Personal Email",
    phone: "Phone",
    department: "Department",
    designation: "Designation",
    role: "Role",
    location: "Location",
    dob: "Date of Birth",
    joining_date: "Joining Date",
    is_active: "Active",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  const HIDDEN_FIELDS = new Set(["avatar_url"]);

  const formatValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (key === "created_at" || key === "updated_at")
      return new Date(value).toLocaleString();
    if (key === "dob" || key === "joining_date")
      return new Date(value).toLocaleDateString();
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const labelOf = (key) =>
    FIELD_LABELS[key] ||
    key.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const groupByEmployee = (data) => {
    if (!data) return {};
    return data.reduce((acc, item) => {
      const name = item.profiles
        ? `${item.profiles.first_name} ${item.profiles.last_name}`
        : "Unknown Employee";
      if (!acc[name]) acc[name] = [];
      acc[name].push(item);
      return acc;
    }, {});
  };

  const filterAttendanceByDate = (data) => {
    if (!data || data.length === 0) return [];

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    return data.filter((item) => {
      if (!item.attendance_date) return false;

      const [yearStr, monthStr] = item.attendance_date.split("-");
      const itemYear = parseInt(yearStr);
      const itemMonth = parseInt(monthStr);

      if (attendanceFilter === "This Month") {
        return itemMonth === currentMonth && itemYear === currentYear;
      } else if (attendanceFilter === "Last Month") {
        let lastMonth = currentMonth - 1;
        let lastYear = currentYear;
        if (lastMonth === 0) {
          lastMonth = 12;
          lastYear = currentYear - 1;
        }
        return itemMonth === lastMonth && itemYear === lastYear;
      }
      return true;
    });
  };

  const ProfileCard = () => (
    <div
      className="profile-card"
      onClick={() => handleProfileClick(currentUser)}
    >
      <div className="avatar-container" onClick={(e) => e.stopPropagation()}>
        <div
          className="avatar-circle"
          onClick={() =>
            currentUser?.avatar_url && openImagePreview(currentUser.avatar_url)
          }
        >
          {currentUser?.avatar_url ? (
            <img src={currentUser.avatar_url} alt="Profile" />
          ) : (
            currentUser?.first_name?.charAt(0) || "U"
          )}
        </div>

        <div className="avatar-actions">
          <label className="avatar-upload-btn">
            {uploadingAvatar ? "Uploading..." : "Change Photo"}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleAvatarChange}
            />
          </label>

          {currentUser?.avatar_url && (
            <button
              type="button"
              className="avatar-remove-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleAvatarDelete();
              }}
              disabled={uploadingAvatar}
            >
              Remove
            </button>
          )}
        </div>
      </div>

      <div className="profile-info">
        <h3>
          {currentUser?.first_name} {currentUser?.last_name}
        </h3>
        <p className="designation">{currentUser?.designation}</p>

        {(isAdmin || !isAdmin) && (
          <div className="status-info">
            <span className="weekend-badge">Active</span>
            <div className="time">{formatTime(elapsedSeconds)}</div>

            <button className="checkin-btn" onClick={handleCheckInClick}>
              {isTimerRunning ? "Check-out" : "Check-in"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderOverviewContent = () => {
    if (overviewTab === "Activities") {
      return (
        <div className="tab-content-card">
          <h2>🏠 Activities</h2>
          <div className="greeting-card">
            <div className="greeting-text">
              <h3>Hello, {currentUser?.first_name}!</h3>
              <p>Here is your daily overview.</p>
            </div>
          </div>
          <div className="work-schedule-section">
            <h3>Recent Updates</h3>
            <p>
              {isAdmin
                ? `Total Leaves Applied: ${leaveHistory.length} | Total Attendance Logs: ${attendanceData.length}`
                : `You have applied for ${leaveHistory.length} leaves recently.`}
            </p>
          </div>

          {/* ✅ UPDATED: User ko "Work Days Remaining" show, Admin me "Remaining Days" remove + manual extra days add */}
          <div className="work-schedule-section">
            <h3>Internship Certificate Eligibility</h3>

            {loading ? (
              <p>Loading...</p>
            ) : isAdmin ? (
              <div className="table-wrapper">
                <table className="leave-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Joining Date</th>
                      <th>Auto Extra Days</th>
                      <th>Manual Extra Days</th>
                      <th>Total Extra Days</th>
                      <th>Eligible On</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reporters || []).map((emp) => {
                      const info = getInternshipEligibility(emp);
                      const draftVal =
                        manualDaysDraft[emp.id] ??
                        String(emp.manual_extra_days ?? 0);

                      return (
                        <tr key={emp.id}>
                          <td>
                            {emp.first_name} {emp.last_name}
                          </td>
                          <td>
                            {emp.joining_date
                              ? String(emp.joining_date).slice(0, 10)
                              : "-"}
                          </td>
                          <td>{info ? info.autoExtraDays : "-"}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              style={{ width: "90px" }}
                              value={draftVal}
                              onChange={(e) =>
                                setManualDaysDraft((prev) => ({
                                  ...prev,
                                  [emp.id]: e.target.value,
                                }))
                              }
                            />
                          </td>
                          <td>{info ? info.extraDays : "-"}</td>
                          <td>
                            {info ? info.actualEnd.toLocaleDateString() : "-"}
                          </td>
                          <td>
                            <button
                              onClick={() => saveManualExtraDays(emp)}
                              disabled={savingManualDaysId === emp.id}
                            >
                              {savingManualDaysId === emp.id
                                ? "Saving..."
                                : "Save"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              (() => {
                const info = getInternshipEligibility(currentUser);
                return (
                  <p>
                    Work Days Remaining:{" "}
                    <strong>{info ? info.remainingDays : "-"}</strong> | Eligible
                    On:{" "}
                    <strong>
                      {info ? info.actualEnd.toLocaleDateString() : "-"}
                    </strong>{" "}
                    | Extra Leave Days:{" "}
                    <strong>{info ? info.extraDays : "-"}</strong>
                  </p>
                );
              })()
            )}
          </div>
          {/* ✅ UPDATED END */}
        </div>
      );
    }

    if (overviewTab === "Profile") {
      if (isAdmin) {
        return (
          <div className="tab-content-card">
            <h2>👥 Employees</h2>
            text
            <div className="employees-grid">
              {reporters.map((u) => (
                <div className="employee-card" key={u.id}>
                  <div className="employee-card-top">
                    <div
                      className="employee-avatar avatar-circle-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (u.avatar_url) openImagePreview(u.avatar_url);
                      }}
                    >
                      {u.avatar_url ? (
                        <img
                          src={u.avatar_url}
                          alt={`${u.first_name} ${u.last_name}`}
                        />
                      ) : (
                        (u.first_name?.[0] || "U").toUpperCase()
                      )}
                    </div>

                    <div className="action-buttons">
                      <button
                        className="edit-btn"
                        title="Edit"
                        onClick={() => openEdit(u)}
                      >
                        ✏️
                      </button>
                      <button
                        className="delete-btn"
                        title="Delete"
                        onClick={() => deleteUser(u)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div className="employee-info">
                    <div className="employee-name">
                      {u.first_name} {u.last_name}
                    </div>
                    <div className="employee-sub">
                      {u.designation || "-"} • {u.department || "-"}
                    </div>
                    <div className="employee-sub">{u.email}</div>
                  </div>
                </div>
              ))}
            </div>
            {editingUser && (
              <div className="modal-backdrop" onClick={closeEdit}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>
                      Edit: {editingUser.first_name} {editingUser.last_name}
                    </h3>
                    <button className="modal-close" onClick={closeEdit}>
                      ✖
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="form-grid">
                      <div className="form-item">
                        <label>First Name</label>
                        <input
                          value={editForm.first_name}
                          onChange={(e) =>
                            handleEditChange("first_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Last Name</label>
                        <input
                          value={editForm.last_name}
                          onChange={(e) =>
                            handleEditChange("last_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Email</label>
                        <input
                          value={editForm.email}
                          onChange={(e) =>
                            handleEditChange("email", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Personal Email</label>
                        <input
                          value={editForm.personal_email}
                          onChange={(e) =>
                            handleEditChange("personal_email", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Phone</label>
                        <input
                          value={editForm.phone}
                          onChange={(e) =>
                            handleEditChange("phone", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          value={editForm.dob}
                          onChange={(e) =>
                            handleEditChange("dob", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Gender</label>
                        <input
                          value={editForm.gender}
                          onChange={(e) =>
                            handleEditChange("gender", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Designation</label>
                        <input
                          value={editForm.designation}
                          onChange={(e) =>
                            handleEditChange("designation", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Department</label>
                        <input
                          value={editForm.department}
                          onChange={(e) =>
                            handleEditChange("department", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Employment Type</label>
                        <input
                          value={editForm.employment_type}
                          onChange={(e) =>
                            handleEditChange("employment_type", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Joining Date</label>
                        <input
                          type="date"
                          value={editForm.joining_date}
                          onChange={(e) =>
                            handleEditChange("joining_date", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Role</label>
                        <input
                          value={editForm.role}
                          onChange={(e) =>
                            handleEditChange("role", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Location</label>
                        <input
                          value={editForm.location}
                          onChange={(e) =>
                            handleEditChange("location", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Manager Name</label>
                        <input
                          value={editForm.manager_name}
                          onChange={(e) =>
                            handleEditChange("manager_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Temp Password</label>
                        <input
                          type="password"
                          value={editForm.temp_password}
                          onChange={(e) =>
                            handleEditChange("temp_password", e.target.value)
                          }
                        />
                      </div>
                      <div
                        className="form-item"
                        style={{ gridColumn: "1 / -1" }}
                      >
                        <label>Notes</label>
                        <textarea
                          value={editForm.notes}
                          onChange={(e) =>
                            handleEditChange("notes", e.target.value)
                          }
                          rows={3}
                        />
                      </div>
                      <div className="form-item checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={!!editForm.is_active}
                            onChange={(e) =>
                              handleEditChange("is_active", e.target.checked)
                            }
                          />
                          Active
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn-secondary"
                      onClick={closeEdit}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      onClick={saveEdit}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      } else {
        // current logged-in user
        const user = currentUser;

        if (!user) {
          return (
            <div className="tab-content-card">
              <h2>👤 Profile</h2>
              <div>No profile data found.</div>
            </div>
          );
        }

        return (
          <div className="tab-content-card">
            <h2>👤 Profile</h2>

            <div className="employee-card">
              <div className="employee-card-top">
                <div
                  className="employee-avatar avatar-circle-small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (user.avatar_url) openImagePreview(user.avatar_url);
                  }}
                >
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={`${user.first_name} ${user.last_name}`}
                    />
                  ) : (
                    (user.first_name?.[0] || "U").toUpperCase()
                  )}
                </div>

                <div className="action-buttons">
                  <button
                    className="edit-btn"
                    title="Edit"
                    onClick={() => openEdit(user)}
                  >
                    ✏️
                  </button>
                </div>
              </div>

              <div className="employee-info">
                <div className="employee-name">
                  {user.first_name} {user.last_name}
                </div>
                <div className="employee-sub">
                  {user.designation || "-"} • {user.department || "-"}
                </div>
                <div className="employee-sub">{user.email}</div>
              </div>
            </div>

            {editingUser && editingUser.id === user.id && (
              <div className="modal-backdrop" onClick={closeEdit}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>
                      Edit: {editingUser.first_name} {editingUser.last_name}
                    </h3>
                    <button className="modal-close" onClick={closeEdit}>
                      ✖
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="form-grid">
                      <div className="form-item">
                        <label>First Name</label>
                        <input
                          value={editForm.first_name}
                          onChange={(e) =>
                            handleEditChange("first_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Last Name</label>
                        <input
                          value={editForm.last_name}
                          onChange={(e) =>
                            handleEditChange("last_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Email</label>
                        <input
                          value={editForm.email}
                          onChange={(e) =>
                            handleEditChange("email", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Personal Email</label>
                        <input
                          value={editForm.personal_email}
                          onChange={(e) =>
                            handleEditChange("personal_email", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Phone</label>
                        <input
                          value={editForm.phone}
                          onChange={(e) =>
                            handleEditChange("phone", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          value={editForm.dob}
                          onChange={(e) =>
                            handleEditChange("dob", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-item">
                        <label>Gender</label>
                        <input
                          value={editForm.gender}
                          onChange={(e) =>
                            handleEditChange("gender", e.target.value)
                          }
                        />
                      </div>

                      {/* Designation: read-only, always uppercase */}
                      <div className="form-item">
                        <label>Designation</label>
                        <input
                          value={
                            editForm.designation
                              ? editForm.designation.toUpperCase()
                              : ""
                          }
                          readOnly
                        />
                      </div>

                      {/* Department: read-only */}
                      <div className="form-item">
                        <label>Department</label>
                        <input value={editForm.department} readOnly />
                      </div>

                      {/* Employment Type: read-only */}
                      <div className="form-item">
                        <label>Employment Type</label>
                        <input value={editForm.employment_type} readOnly />
                      </div>

                      {/* Joining Date: disabled */}
                      <div className="form-item">
                        <label>Joining Date</label>
                        <input
                          type="date"
                          value={editForm.joining_date}
                          disabled
                        />
                      </div>

                      {/* Role: read-only */}
                      <div className="form-item">
                        <label>Role</label>
                        <input value={editForm.role} readOnly />
                      </div>

                      <div className="form-item">
                        <label>Location</label>
                        <input
                          value={editForm.location}
                          onChange={(e) =>
                            handleEditChange("location", e.target.value)
                          }
                        />
                      </div>

                      {/* Manager Name: read-only */}
                      <div className="form-item">
                        <label>Manager Name</label>
                        <input value={editForm.manager_name} readOnly />
                      </div>

                      {/* Temp Password + Notes user se hata diye */}

                      <div className="form-item checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={!!editForm.is_active}
                            onChange={(e) =>
                              handleEditChange("is_active", e.target.checked)
                            }
                          />
                          Active
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn-secondary"
                      onClick={closeEdit}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      onClick={saveEdit}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }
    }

    if (overviewTab === "Leave") {
      if (isAdmin) {
        const groupedLeaves = groupByEmployee(leaveHistory);
        return (
          <div className="tab-content-card">
            <h2>🏖️ Employee Leaves</h2>
            <div className="leave-history-section">
              {Object.keys(groupedLeaves).length === 0 ? (
                <p>No leaves found.</p>
              ) : (
                Object.keys(groupedLeaves).map((employeeName, idx) => (
                  <details
                    key={idx}
                    style={{
                      marginBottom: "10px",
                      border: "1px solid #eee",
                      borderRadius: "5px",
                      padding: "5px",
                    }}
                  >
                    <summary
                      style={{
                        padding: "10px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        background: "#f9f9f9",
                      }}
                    >
                      {employeeName}{" "}
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        ({groupedLeaves[employeeName].length} records)
                      </span>
                    </summary>
                    <div style={{ padding: "10px" }}>
                      <table className="leave-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Start Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedLeaves[employeeName].map((leave, i) => (
                            <tr key={i}>
                              <td>{leave.leave_type}</td>
                              <td>{leave.start_date}</td>
                              <td>
                                <span
                                  className={`status-tag ${leave.status?.toLowerCase()}`}
                                >
                                  {leave.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                ))
              )}
            </div>
          </div>
        );
      }

      return (
        <div className="tab-content-card">
          <h2>🏖️ My Leave History</h2>
          <div className="leave-history-section">
            {leaveHistory.length === 0 ? (
              <p>No records found.</p>
            ) : (
              <table className="leave-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Start Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveHistory.map((leave, i) => (
                    <tr key={i}>
                      <td>{leave.leave_type}</td>
                      <td>{leave.start_date}</td>
                      <td>
                        <span
                          className={`status-tag ${leave.status?.toLowerCase()}`}
                        >
                          {leave.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      );
    }

    if (overviewTab === "Attendance") {
      const filteredData = filterAttendanceByDate(attendanceData);

      // ✅ Per‑day login duration helper (sirf check‑out ke baad)
      const getLoginDuration = (row) => {
        if (!row.check_in_time || !row.check_out_time) return "-";
        const diffMs =
          new Date(row.check_out_time) - new Date(row.check_in_time);
        if (diffMs <= 0) return "-";
        const totalSec = Math.floor(diffMs / 1000);
        const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
        const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
        return `${h}:${m}`;
      };

      // ============ ADMIN VIEW ============
      if (isAdmin) {
        const groupedAttendance = groupByEmployee(filteredData);

        return (
          <div className="tab-content-card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h2>📊 Employee Attendance</h2>
              <div style={{ display: "flex", gap: "5px" }}>
                <button
                  onClick={() => setAttendanceFilter("This Month")}
                  style={{
                    padding: "5px 10px",
                    cursor: "pointer",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    background:
                      attendanceFilter === "This Month" ? "#007bff" : "#fff",
                    color: attendanceFilter === "This Month" ? "#fff" : "#000",
                  }}
                >
                  This Month
                </button>
                <button
                  onClick={() => setAttendanceFilter("Last Month")}
                  style={{
                    padding: "5px 10px",
                    cursor: "pointer",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    background:
                      attendanceFilter === "Last Month" ? "#007bff" : "#fff",
                    color: attendanceFilter === "Last Month" ? "#fff" : "#000",
                  }}
                >
                  Last Month
                </button>
              </div>
            </div>

            <div className="table-wrapper">
              {Object.keys(groupedAttendance).length === 0 ? (
                <p>No records found for {attendanceFilter}.</p>
              ) : (
                Object.keys(groupedAttendance).map((employeeName, idx) => {
                  const rows = groupedAttendance[employeeName];

                  return (
                    <details
                      key={idx}
                      style={{
                        marginBottom: "10px",
                        border: "1px solid #eee",
                        borderRadius: "5px",
                        padding: "5px",
                      }}
                    >
                      <summary
                        style={{
                          padding: "10px",
                          cursor: "pointer",
                          fontWeight: "bold",
                          background: "#f9f9f9",
                        }}
                      >
                        {employeeName}{" "}
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          ({rows.length} days)
                        </span>
                      </summary>

                      <div style={{ padding: "10px" }}>
                        <table className="attendance-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>In</th>
                              <th>Out</th>
                              <th>Login Hours</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, i) => (
                              <tr key={i}>
                                <td>{row.attendance_date}</td>
                                <td>
                                  {new Date(
                                    row.check_in_time,
                                  ).toLocaleTimeString()}
                                </td>
                                <td>
                                  {row.check_out_time
                                    ? new Date(
                                      row.check_out_time,
                                    ).toLocaleTimeString()
                                    : "-"}
                                </td>
                                <td>{getLoginDuration(row)}</td>
                                <td>{row.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  );
                })
              )}
            </div>
          </div>
        );
      }

      // ============ USER VIEW ============
      return (
        <div className="tab-content-card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2>📊 My Attendance Log</h2>
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                onClick={() => setAttendanceFilter("This Month")}
                style={{
                  padding: "5px",
                  background:
                    attendanceFilter === "This Month" ? "#eee" : "transparent",
                }}
              >
                This Month
              </button>
              <button
                onClick={() => setAttendanceFilter("Last Month")}
                style={{
                  padding: "5px",
                  background:
                    attendanceFilter === "Last Month" ? "#eee" : "transparent",
                }}
              >
                Last Month
              </button>
            </div>
          </div>

          <div className="attendance-stats-grid">
            <div className="stat-box">
              Total Present ({attendanceFilter}): {filteredData.length}
            </div>
          </div>

          <div className="table-wrapper">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Login Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, i) => (
                  <tr key={i}>
                    <td>{row.attendance_date}</td>
                    <td>{new Date(row.check_in_time).toLocaleTimeString()}</td>
                    <td>
                      {row.check_out_time
                        ? new Date(row.check_out_time).toLocaleTimeString()
                        : "-"}
                    </td>
                    <td>{getLoginDuration(row)}</td>
                    <td>{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // ===== renderOverviewContent ka fallback (yaha se niche kuch bhi change nahi kiya) =====
    return null;
  };
  

  return (
    <div className="overview-container">
      {/* LEFT PANEL */}
      <div className="left-panel">
        {!isMobile && <ProfileCard />}

        <div className="reporters-section">
          <h3>{isAdmin ? "All Employees" : "Team Members"}</h3>
          

          {reporters
           .filter((person) => person.is_active !== false) 
          .map((person) => {
            const isCheckedInToday = todayCheckedInIds.includes(person.id);

            return (
              <div
                key={person.id}
                className="reporter"
                onClick={() => handleProfileClick(person)}
                style={{
                  cursor:
                    isAdmin || person.id === currentUser?.id
                      ? "pointer"
                      : "default",
                }}
              >
                <div className="reporter-avatar">
                  {person.first_name?.charAt(0)}
                </div>

                <div className="reporter-details">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <strong>
                      {person.first_name} {person.last_name}
                    </strong>

                    {isCheckedInToday && <span className="in-badge">IN</span>}
                  </div>

                  {isAdmin && (
                    <span className="weekend-text">
                      {person.designation || "-"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* RIGHT/MAIN PANEL */}

      <div
        className={`activities-panel ${activeMainTab === "Org Chart" ? "hierarchy-mode" : ""
          }`}
      >
        <div className="main-tabs">
          {mainTabs.map((tab) => (
            <button
              key={tab}
              className={`main-tab ${activeMainTab === tab ? "active" : ""}`}
              onClick={() => handleMainTabChange(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {isMobile && activeMainTab === "Overview" && <ProfileCard />}

        {/* Profile Detail Overlay */}
        {selectedProfile ? (
          <div className="profile-detail-card">
            <button onClick={handleBack} className="back-button">
              ← Back
            </button>
            <h2>{selectedProfile.first_name}'s Profile</h2>

            <div className="profile-fields-grid">
              {Object.entries(selectedProfile)
                .filter(([key]) => !HIDDEN_FIELDS.has(key))
                .map(([key, value]) => (
                  <div className="profile-field" key={key}>
                    <div className="profile-label">{labelOf(key)}</div>
                    <div className="profile-value">
                      {formatValue(key, value)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <>
            {activeMainTab !== "Org Chart" && (
              <div className="sub-tabs">
                {activeMainTab === "Overview" &&
                  overviewTabs.map((tab) => (
                    <button
                      key={tab}
                      className={`sub-tab ${overviewTab === tab ? "active" : ""
                        }`}
                      onClick={() => setOverviewTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                {activeMainTab === "Dashboard" &&
                  dashboardTabs.map((tab) => (
                    <button
                      key={tab}
                      className={`sub-tab ${dashboardTab === tab ? "active" : ""
                        }`}
                      onClick={() => setDashboardTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                {activeMainTab === "Calendar" &&
                  calendarTabs.map((tab) => (
                    <button
                      key={tab}
                      className={`sub-tab ${calendarTab === tab ? "active" : ""
                        }`}
                      onClick={() => setCalendarTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
              </div>
            )}

            {activeMainTab === "Overview" && renderOverviewContent()}
            {activeMainTab === "Dashboard" && <DashboardTab />}
            {activeMainTab === "Calendar" && <Calendar view={calendarTab} />}
            {activeMainTab === "Org Chart" && <HierarchyPage />}
          </>
        )}
      </div>
    </div>
  );
}

export default Overview;